import { useState, useMemo, useEffect } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import api from '../api/client'
import { listasApi } from '../api/listaPrecios'

/**
 * Modal de "compartir con el cliente" — se abre tras confirmar un remito (o
 * desde el botón ✉ del toolbar). El operador tilda qué comprobantes incluir
 * (factura, recibo(s), link de seguimiento), opcionalmente edita el mensaje
 * institucional, y elige el canal: Mail (mailto:) o WhatsApp (wa.me).
 *
 * IMPORTANTE: mailto/wa.me NO soportan adjuntos (limitación del estándar).
 * Lo que viaja en el cuerpo son LINKS PÚBLICOS a los PDFs (por token de
 * seguimiento del remito — quien tenga el link, ve solo el pedido puntual).
 *
 * Contenido enriquecido: saludo personalizado + resumen del pedido (nº, factura,
 * importe, items, dirección de entrega) + bloque de comprobantes con sus links
 * + footer institucional dinámico (lee config/services.institucional via API).
 *
 * Props:
 *  - abierto, onClose
 *  - remito { id, numero_presea, token_seguimiento, ... }
 *  - nv { id, numero_lgr, numero_presea (factura), total, items, direccion_envio, pagos, ... }
 *  - cliente { nombre, email, telefono, telefono_wa, ... }
 */
export default function ModalCompartirCliente({ abierto, onClose, remito, nv, cliente: clienteProp }) {
  const cliente = clienteProp || nv?.cliente
  const token = remito?.token_seguimiento

  const recibos = (nv?.pagos || []).filter(p => p.numero_presea && p.estado === 'acreditado')
  const hayFactura = Boolean(nv?.numero_presea)
  // NV "solo remito": la factura no existe por diseño — el envío al cliente
  // sale igual, con el remito y el link de seguimiento.
  const sinFactura = Boolean(nv?.sin_factura)

  // Mail de destino EDITABLE (Martin, 11 sep 2026: "dejame poner el mail que
  // quiero, y que ese correo lo herede por el resto del pedido"): arranca en
  // el contacto vigente del pedido y, si el operador lo cambia, se persiste
  // como contacto del pedido ANTES de enviar — comprobantes, avisos de entrega
  // y encuesta salen ahí de acá en más (NotaVenta::emailContacto()).
  const emailInicial = nv?.email_contacto || cliente?.email || ''
  const [email, setEmail] = useState(emailInicial)
  const emailValido = /.+@.+\..+/.test(email.trim())

  const persistirEmailSiCambio = async () => {
    const e = email.trim()
    if (!nv?.id || !e || e.toLowerCase() === String(emailInicial).toLowerCase()) return
    try { await api.put(`/notas-venta/${nv.id}/email-contacto`, { email: e }) } catch { /* el envío directo lo persiste igual en el backend */ }
  }

  const [incluyeFactura, setIncluyeFactura] = useState(true)
  const [recibosSel, setRecibosSel] = useState(() => new Set(recibos.map(r => r.id)))
  const [incluyeTracking, setIncluyeTracking] = useState(true)
  const [incluyeResumen, setIncluyeResumen] = useState(true)
  const [mensaje, setMensaje] = useState(() => mensajeDefault(cliente?.nombre, nv?.numero_lgr))

  // Datos institucionales (footer). Vienen del backend (config/services.php
  // → INSTITUCIONAL_*). Si el endpoint falla, el footer se omite gracefully.
  const [institucional, setInstitucional] = useState(null)
  // Teléfono / WhatsApp del canal de atención de la lista del cliente. Cada
  // lista tiene su tel (LISTA1 mayorista, LISTADIST distribución, LISTALM...).
  // Se busca por código_presea contra la lista del cliente.
  const [telefonoLista, setTelefonoLista] = useState(null)

  // Envío directo por el servidor: mail institucional con los PDFs adjuntos
  // (factura + remito + recibo). A diferencia de "Abrir Mail"/"WhatsApp" (que
  // sólo llevan links por mailto/wa.me), este sale de Vinoteca Ligier vía Brevo.
  const [enviando, setEnviando] = useState(false)
  const [resultadoEnvio, setResultadoEnvio] = useState(null)

  useEffect(() => {
    if (!abierto) return
    api.get('/publico/institucional').then(({ data }) => setInstitucional(data || {})).catch(() => setInstitucional({}))
    listasApi.listar().then(({ data }) => {
      const lista = (data || []).find(l => l.codigo_presea === cliente?.lista_precios || l.codigo === cliente?.lista_precios)
      setTelefonoLista(lista?.telefono || null)
    }).catch(() => setTelefonoLista(null))
  }, [abierto, cliente?.lista_precios])

  const origen = typeof window !== 'undefined' ? window.location.origin : ''
  const linkFactura  = token && hayFactura ? `${origen}/api/publico/seguimiento/${token}/factura.pdf` : null
  const linkRecibo   = (pago) => `${origen}/api/publico/seguimiento/${token}/recibos/${pago.id}.pdf`
  const linkTracking = token ? `${origen}/seguimiento/${token}` : null

  // Cuerpo del mail (plain text) y de WhatsApp (con *negrita*). Mismo contenido,
  // distinto énfasis. Se rearman en cada cambio (useMemo barato).
  const cuerpoMail = useMemo(() => armarCuerpo('mail'), [mensaje, incluyeFactura, recibosSel, incluyeTracking, incluyeResumen, institucional, telefonoLista])
  const cuerpoWa = useMemo(() => armarCuerpo('wa'), [mensaje, incluyeFactura, recibosSel, incluyeTracking, incluyeResumen, institucional, telefonoLista])

  function armarCuerpo(canal) {
    // En WA mantenemos *negrita* solo para los títulos de bloque (es el
    // estándar de WhatsApp); en mail va plain.
    const b = canal === 'wa' ? (s) => `*${s}*` : (s) => s

    const partes = [mensaje.trim()]

    if (incluyeResumen) {
      const items = (nv?.items || [])
      const itemsResumen = items.length === 0 ? '' :
        items.length === 1 ? `1 · ${items[0].articulo?.descripcion || ''}`.trim() :
        `${items.length} productos`
      const total = Number(nv?.total || 0)
      const tipo = nv?.tipo_factura ? `${nv.tipo_factura} ` : ''
      const dirEnvio = nv?.direccion_envio
      const dir = (dirEnvio && (dirEnvio.calle || dirEnvio.localidad))
        ? [dirEnvio.calle, dirEnvio.localidad, dirEnvio.provincia, dirEnvio.codigo_postal ? `CP ${dirEnvio.codigo_postal}` : null].filter(Boolean).join(' · ')
        : null

      partes.push('', `- ${b('RESUMEN DEL PEDIDO')}`, '')
      if (nv?.numero_lgr) partes.push(`Pedido:   ${nv.numero_lgr}`)
      if (hayFactura) partes.push(`Factura:  ${tipo}${nv.numero_presea}`)
      // Sin factura (solo remito) no se comunica importe: es un envío de
      // mercadería, no una venta facturada.
      if (total > 0 && hayFactura) partes.push(`Importe:  $${total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      if (itemsResumen) partes.push(`Items:    ${itemsResumen}`)
      if (dir) partes.push(`Entrega:  ${dir}`)
    }

    const links = []
    if (incluyeFactura && linkFactura) links.push(`📄 Factura\n${linkFactura}`)
    recibos.filter(p => recibosSel.has(p.id)).forEach(p => {
      links.push(`🧾 Recibo ${p.numero_presea}\n${linkRecibo(p)}`)
    })
    if (incluyeTracking && linkTracking) links.push(`📦 Seguí tu pedido en tiempo real\n${linkTracking}`)

    if (links.length > 0) {
      partes.push('', `- ${b('COMPROBANTES Y SEGUIMIENTO')}`, '')
      links.forEach((l, i) => {
        partes.push(l)
        if (i < links.length - 1) partes.push('')
      })
    }

    // Cierre + firma institucional. Estilo plano, sin emojis decorativos en el
    // footer — el cliente lo ve más serio. Teléfono / WhatsApp viene de la
    // lista del cliente (no del .env), cada lista tiene su canal.
    partes.push('', 'Cualquier consulta, respondé este mensaje y te contestamos.', '', '¡Muchas gracias!', b(institucional?.nombre || 'Vinoteca Ligier'))

    const i = institucional || {}
    const footerLineas = []
    if (i.direccion) footerLineas.push(i.direccion)
    if (telefonoLista) {
      const telLimpio = telefonoLista.replace(/\D/g, '')
      footerLineas.push(`WhatsApp: https://wa.me/${telLimpio}`)
    }
    if (i.web) footerLineas.push(i.web)
    if (i.email) footerLineas.push(i.email)
    if (i.instagram) footerLineas.push(`Seguinos en Instagram: ${i.instagram}`)
    if (i.facebook) footerLineas.push(`Facebook: ${i.facebook}`)

    if (footerLineas.length > 0) {
      partes.push('')
      partes.push(...footerLineas)
    }

    return partes.join('\n')
  }

  const toggleRecibo = (id) => setRecibosSel(prev => {
    const s = new Set(prev)
    if (s.has(id)) s.delete(id); else s.add(id)
    return s
  })

  const algoSeleccionado =
    (incluyeFactura && linkFactura) ||
    recibos.some(p => recibosSel.has(p.id)) ||
    (incluyeTracking && linkTracking)

  // Envío directo (mail con PDFs adjuntos): requiere remito confirmado (o
  // posterior) + factura + remito de Presea emitidos + email del cliente. El
  // backend revalida todo — esto es solo la guía de habilitación del botón.
  const remitoConfirmado = Boolean(remito) && !['ingresado', 'anulado'].includes(remito?.estado)
  const hayRemitoPresea = Boolean(nv?.numero_remito_presea)
  const motivoEnvio =
    !emailValido ? 'Cargá un email de destino.' :
    !remitoConfirmado ? 'El remito todavía no está confirmado.' :
    (!hayFactura && !sinFactura) ? 'La factura de Presea aún no está emitida.' :
    !hayRemitoPresea ? 'El remito de Presea aún no está generado.' : null

  const enviarComprobantes = async () => {
    if (!remito?.id || motivoEnvio) return
    setEnviando(true); setResultadoEnvio(null)
    try {
      const { data } = await api.post(`/remitos/${remito.id}/enviar-comprobantes`, { email: email.trim() })
      setResultadoEnvio({ ok: true, msg: data?.message || `Comprobantes enviados a ${email.trim()}.` })
    } catch (e) {
      setResultadoEnvio({ ok: false, msg: e?.response?.data?.message || 'No se pudieron enviar los comprobantes.' })
    } finally {
      setEnviando(false)
    }
  }

  const abrirMail = async () => {
    if (!emailValido) { window.alert('Cargá un email de destino.'); return }
    await persistirEmailSiCambio()
    const asunto = nv?.numero_lgr
      ? `Tu pedido ${nv.numero_lgr} — comprobantes y seguimiento`
      : 'Tu compra en Vinoteca Ligier — comprobantes y seguimiento'
    const url = `mailto:${encodeURIComponent(email.trim())}?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpoMail)}`
    window.location.href = url
    onClose()
  }

  const abrirWhatsApp = () => {
    const tel = (cliente?.telefono_wa || cliente?.telefono || '').replace(/\D/g, '')
    if (!tel) { window.alert('El cliente no tiene teléfono cargado.'); return }
    const destino = tel.startsWith('54') ? tel : '54' + tel
    const url = `https://wa.me/${destino}?text=${encodeURIComponent(cuerpoWa)}`
    window.open(url, '_blank', 'noopener,noreferrer')
    onClose()
  }

  if (!abierto) return null

  // Avisos al operador si falta info para el footer.
  const faltaDireccion = institucional && !institucional.direccion
  const faltaTelLista = cliente?.lista_precios && telefonoLista === null && institucional !== null
  const faltanDatosInstitucionales = faltaDireccion || faltaTelLista

  return (
    <Modal title="Compartir comprobantes con el cliente" onClose={onClose} maxWidth={680}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Destinatario: el mail es EDITABLE y hereda para el resto del pedido */}
        <div style={st.bloque}>
          <div style={st.lbl}>Destinatario</div>
          <div style={st.cliInfo}>
            <strong>{cliente?.nombre || '—'}</strong>
            {(cliente?.telefono_wa || cliente?.telefono) && <span style={st.dato}>📱 {cliente.telefono_wa || cliente.telefono}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span>📧</span>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="email@destino.com"
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
            />
          </div>
          {email.trim() && email.trim().toLowerCase() !== String(emailInicial).toLowerCase() && (
            <div style={st.hint}>Este mail queda como <strong>contacto del pedido</strong>: los comprobantes, los avisos de entrega y la encuesta salen ahí de acá en más.</div>
          )}
          {nv?.email_contacto && cliente?.email && nv.email_contacto.toLowerCase() !== cliente.email.toLowerCase() && (
            <div style={st.hint}>Contacto elegido para este pedido (la ficha facturada dice {cliente.email}).</div>
          )}
        </div>

        {/* Qué incluir */}
        <div style={st.bloque}>
          <div style={st.lbl}>¿Qué incluir?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            <label style={st.check}>
              <input type="checkbox" checked={incluyeResumen} onChange={() => setIncluyeResumen(v => !v)} />
              <span>📋 Resumen del pedido (nº, factura, importe, items, dirección)</span>
            </label>
            {hayFactura ? (
              <label style={st.check}>
                <input type="checkbox" checked={incluyeFactura} onChange={() => setIncluyeFactura(v => !v)} />
                <span>📄 Factura <code style={st.mono}>{nv.numero_presea}</code> (link)</span>
              </label>
            ) : (
              <span style={st.dis}>📄 Factura — {sinFactura ? 'no corresponde (solo remito)' : 'aún no emitida'}</span>
            )}
            {recibos.length === 0 ? (
              <span style={st.dis}>🧾 Recibos — sin cobros acreditados</span>
            ) : recibos.map(p => (
              <label key={p.id} style={st.check}>
                <input type="checkbox" checked={recibosSel.has(p.id)} onChange={() => toggleRecibo(p.id)} />
                <span>🧾 Recibo <code style={st.mono}>{p.numero_presea}</code> · ${Number(p.importe).toLocaleString('es-AR')} (link)</span>
              </label>
            ))}
            <label style={st.check}>
              <input type="checkbox" checked={incluyeTracking} onChange={() => setIncluyeTracking(v => !v)} disabled={!linkTracking} />
              <span>📦 Link de seguimiento del pedido</span>
            </label>
          </div>
        </div>

        {/* Mensaje editable */}
        <div style={st.bloque}>
          <div style={st.lbl}>Saludo (editable)</div>
          <textarea value={mensaje} onChange={e => setMensaje(e.target.value)} style={st.textarea} rows={4} />
          <div style={st.hint}>El resumen, los links y el footer institucional se agregan automáticamente abajo.</div>
        </div>

        {/* Aviso si faltan datos institucionales */}
        {faltanDatosInstitucionales && (
          <div style={st.warnBox}>
            ⚠ El footer no va a tener algunos datos:
            {faltaDireccion && <> dirección (cargá <code>INSTITUCIONAL_DIRECCION</code> en <code>.env</code>);</>}
            {faltaTelLista && <> teléfono de la lista {cliente.lista_precios} (cargalo en <code>/listas-precios</code>);</>}
          </div>
        )}

        {/* Preview del cuerpo */}
        <details style={st.preview} open>
          <summary style={st.previewSummary}>Vista previa del mensaje</summary>
          <pre style={st.previewPre}>{cuerpoMail}</pre>
          <div style={st.hint}>WhatsApp usa el mismo contenido con <code>*negrita*</code> en los títulos.</div>
        </details>

        {/* Envío directo por el servidor: mail institucional de Vinoteca Ligier
            con la factura, el remito y el/los recibo(s) como PDFs ADJUNTOS +
            link de seguimiento y portal. A diferencia de "Abrir Mail" (mailto,
            solo links), este sale del sistema. */}
        <div style={st.bloque}>
          <div style={st.lbl}>Enviar por correo con adjuntos</div>
          <div style={st.hint}>
            {sinFactura && !hayFactura
              ? 'Sale de Vinoteca Ligier con el resumen del pedido + el link de seguimiento. No necesita abrir tu casilla.'
              : 'Sale de Vinoteca Ligier con la factura y el recibo adjuntos (PDF) + el link de seguimiento. No necesita abrir tu casilla.'}
          </div>
          {motivoEnvio && <div style={{ ...st.dis, marginTop: 6 }}>⚠ {motivoEnvio}</div>}
          {resultadoEnvio && (
            <div style={{ ...(resultadoEnvio.ok ? st.okBox : st.warnBox), marginTop: 8 }}>
              {resultadoEnvio.ok ? '✓ ' : '⚠ '}{resultadoEnvio.msg}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Boton variante="send" sm disabled={Boolean(motivoEnvio) || enviando} onClick={enviarComprobantes}>
              {enviando ? 'Enviando…' : '📧 Enviar comprobantes al cliente'}
            </Boton>
          </div>
        </div>

        {/* Acciones */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Boton variante="ghost" sm onClick={onClose}>Cancelar</Boton>
          <Boton variante="secondary" sm disabled={!algoSeleccionado || !emailValido} onClick={abrirMail}>
            📧 Abrir Mail
          </Boton>
          <Boton variante="success" sm disabled={!algoSeleccionado || !(cliente?.telefono_wa || cliente?.telefono)} onClick={abrirWhatsApp}>
            💬 Abrir WhatsApp
          </Boton>
        </div>
      </div>
    </Modal>
  )
}

function mensajeDefault(nombreCliente, numeroLgr) {
  const saludo = nombreCliente ? `Hola ${nombreCliente.split(' ')[0]},` : '¡Hola!'
  return `${saludo}

¡Gracias por tu compra en Vinoteca Ligier! 🍷

Te dejamos el resumen de tu pedido${numeroLgr ? ` ${numeroLgr}` : ''} y los comprobantes para que los tengas a mano.`
}

const st = {
  bloque: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' },
  lbl: { fontWeight: 600, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' },
  cliInfo: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, fontSize: '0.875rem' },
  dato: { color: '#64748b', fontSize: '0.82rem' },
  warn: { color: '#92400e', fontSize: '0.82rem', fontWeight: 600 },
  warnBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', color: '#92400e' },
  okBox: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', color: '#166534' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', cursor: 'pointer', userSelect: 'none' },
  dis: { color: '#94a3b8', fontSize: '0.875rem', fontStyle: 'italic' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.82rem', color: '#1e40af' },
  textarea: { width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  hint: { fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 },
  preview: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' },
  previewSummary: { cursor: 'pointer', fontSize: '0.82rem', color: '#475569', userSelect: 'none', fontWeight: 600 },
  previewPre: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.78rem', color: '#0f172a', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginTop: 8, maxHeight: 400, overflowY: 'auto', lineHeight: 1.5 },
}
